import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Globe, Sparkles, Video, Users, FileText, Settings, Plus,
  Eye, Edit, Trash2, Play, Pause, Download, Share2, Copy,
  Instagram, Facebook, Linkedin, Youtube, TrendingUp,
  Palette, Layout, ShoppingCart, Megaphone, Mail, MessageSquare,
  Image, Mic, MicOff, Camera, Film, Wand2, RefreshCw, Loader2,
  Check, X, ChevronRight, ChevronDown, ExternalLink, BarChart3, Target,
  Zap, Clock, Calendar, Send, Heart, MessageCircle, Bookmark,
  HelpCircle, BookOpen, Lightbulb, ArrowRight, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== COMPOSANT DICTÉE VOCALE ====================

const VoiceInput = ({ onTranscript, className = "" }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.lang = 'fr-FR';

    recognitionRef.current.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onTranscript(transcript);
      setIsListening(false);
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Erreur reconnaissance vocale:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Accès au microphone refusé');
      }
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [onTranscript]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('La dictée vocale n\'est pas supportée par votre navigateur');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast.info('Parlez maintenant...', { duration: 2000 });
      } catch (err) {
        console.error('Erreur démarrage:', err);
        toast.error('Impossible de démarrer la dictée');
      }
    }
  };

  if (!isSupported) return null;

  return (
    <Button
      type="button"
      variant={isListening ? "destructive" : "outline"}
      size="icon"
      onClick={toggleListening}
      className={`shrink-0 ${className}`}
      title={isListening ? "Arrêter la dictée" : "Dicter (cliquez et parlez)"}
    >
      {isListening ? (
        <MicOff className="w-4 h-4 animate-pulse" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </Button>
  );
};

// Composant Avatar Influenceur
const InfluenceurAvatar = ({ influenceur, size = "md" }) => {
  const sizeClasses = {
    sm: "w-10 h-10",
    md: "w-16 h-16",
    lg: "w-24 h-24"
  };
  
  const avatarColors = {
    "agriculteur": "from-green-400 to-emerald-600",
    "expert_bio": "from-lime-400 to-green-600",
    "chef": "from-orange-400 to-red-600",
    "default": "from-blue-400 to-indigo-600"
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${avatarColors[influenceur?.persona] || avatarColors.default} flex items-center justify-center text-white font-bold shadow-lg`}>
      {influenceur?.nom?.charAt(0) || "?"}
    </div>
  );
};

// Composant Card Template Site
const TemplateCard = ({ template, onSelect, selected }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-lg ${selected ? 'ring-2 ring-primary border-primary' : ''}`}
    onClick={() => onSelect(template)}
  >
    <div className="aspect-video bg-gradient-to-br from-muted to-muted/50 rounded-t-lg flex items-center justify-center">
      {template.type_site === 'ecommerce' ? (
        <ShoppingCart className="w-12 h-12 text-muted-foreground/50" />
      ) : template.type_site === 'landing' ? (
        <Megaphone className="w-12 h-12 text-muted-foreground/50" />
      ) : (
        <Globe className="w-12 h-12 text-muted-foreground/50" />
      )}
    </div>
    <CardContent className="p-4">
      <h3 className="font-semibold">{template.nom}</h3>
      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
      <div className="flex flex-wrap gap-1 mt-3">
        {template.features?.slice(0, 3).map((f, i) => (
          <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Composant Card Influenceur
const InfluenceurCard = ({ influenceur, onSelect, selected }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-lg ${selected ? 'ring-2 ring-primary border-primary' : ''}`}
    onClick={() => onSelect(influenceur)}
  >
    <CardContent className="p-4 flex items-start gap-4">
      <InfluenceurAvatar influenceur={influenceur} size="md" />
      <div className="flex-1">
        <h3 className="font-semibold">{influenceur.nom}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{influenceur.description}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {influenceur.specialites?.slice(0, 2).map((s, i) => (
            <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Composant Guide Step
const GuideStep = ({ numero, titre, description }) => (
  <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
      {numero}
    </div>
    <div>
      <h4 className="font-semibold">{titre}</h4>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
    </div>
  </div>
);

// Composant Video Card avec statut
const VideoCard = ({ video, onRefresh, onRetry, onDelete }) => {
  const statusColors = {
    'en_cours': 'bg-yellow-500',
    'termine': 'bg-green-500',
    'erreur': 'bg-red-500',
    'bloquee': 'bg-orange-500',
    'en_attente': 'bg-gray-500'
  };
  
  const statusLabels = {
    'en_cours': 'En cours...',
    'termine': 'Terminé',
    'erreur': 'Erreur',
    'bloquee': 'Bloquée',
    'en_attente': 'En attente'
  };
  
  const [showScript, setShowScript] = useState(false);
  const [retrying, setRetrying] = useState(false);
  
  const handleRetry = async () => {
    if (onRetry) {
      setRetrying(true);
      await onRetry(video.id);
      setRetrying(false);
    }
  };
  
  return (
    <Card className="overflow-hidden">
      <div className="aspect-video bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-950/20 dark:to-purple-950/20 relative flex items-center justify-center">
        {video.url_video ? (
          <video 
            src={`${process.env.REACT_APP_BACKEND_URL}${video.url_video}`}
            className="w-full h-full object-cover"
            controls
            poster={`${process.env.REACT_APP_BACKEND_URL}${video.url_video}?poster=true`}
          />
        ) : (
          <div className="text-center p-4">
            <Film className="w-12 h-12 mx-auto text-pink-400 mb-2" />
            {video.statut === 'en_cours' && (
              <>
                <Progress value={video.progression || 0} className="w-32 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{video.progression || 0}% - {video.message_statut || 'Génération en cours...'}</p>
              </>
            )}
            {(video.statut === 'bloquee' || (video.statut === 'en_cours' && video.progression === 60)) && (
              <div>
                <p className="text-sm text-orange-600 mb-1">Génération interrompue</p>
                <p className="text-xs text-muted-foreground">Cliquez sur Relancer</p>
              </div>
            )}
            {video.statut === 'termine' && !video.url_video && (
              <div>
                <p className="text-sm text-amber-600 mb-1">Script généré</p>
                <p className="text-xs text-muted-foreground">Vidéo non disponible</p>
              </div>
            )}
            {video.statut === 'erreur' && (
              <p className="text-sm text-red-500">{video.erreur || video.erreur_video || 'Erreur de génération'}</p>
            )}
          </div>
        )}
        <Badge className={`absolute top-2 right-2 ${statusColors[video.statut] || 'bg-gray-500'}`}>
          {statusLabels[video.statut] || video.statut}
        </Badge>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold line-clamp-2">{video.sujet || 'Vidéo IA'}</h3>
            <p className="text-sm text-muted-foreground">{video.influenceur_nom || 'Influenceur personnalisé'}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {video.duree_reelle || video.duree_cible}s - {video.type_video}
            </p>
          </div>
          {onDelete && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(video.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Script si disponible */}
        {video.script && (
          <div className="mt-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowScript(!showScript)}
              className="text-xs p-0 h-auto"
            >
              {showScript ? 'Masquer le script' : 'Voir le script'}
              <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showScript ? 'rotate-180' : ''}`} />
            </Button>
            {showScript && (
              <div className="mt-2 p-3 bg-muted rounded-lg text-xs max-h-40 overflow-y-auto">
                <pre className="whitespace-pre-wrap font-sans">
                  {typeof video.script === 'string' ? video.script : (video.script.script || JSON.stringify(video.script, null, 2))}
                </pre>
              </div>
            )}
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mt-4">
          {/* Bouton Relancer pour vidéos bloquées ou en erreur */}
          {(video.statut === 'bloquee' || video.statut === 'erreur' || (video.statut === 'en_cours' && video.progression >= 50)) && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleRetry}
              disabled={retrying}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {retrying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Relancer
            </Button>
          )}
          {video.statut === 'en_cours' && video.progression < 50 && (
            <Button variant="outline" size="sm" onClick={() => onRefresh(video.id)}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualiser
            </Button>
          )}
          {video.url_video && (
            <>
              <Button variant="outline" size="sm" asChild>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${video.url_video}`} target="_blank" rel="noopener noreferrer">
                  <Play className="w-4 h-4 mr-1" />
                  Voir
                </a>
              </Button>
              <Button variant="default" size="sm" asChild className="bg-gradient-to-r from-pink-500 to-purple-500">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${video.url_video}`} download={`video_${video.id}.mp4`}>
                  <Download className="w-4 h-4 mr-1" />
                  Télécharger
                </a>
              </Button>
            </>
          )}
          {video.script && !video.url_video && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const scriptText = typeof video.script === 'string' ? video.script : (video.script.script || JSON.stringify(video.script));
                navigator.clipboard.writeText(scriptText);
                toast.success('Script copié !');
              }}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copier le script
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Page principale
export default function StudioIAPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  // Sites
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newSite, setNewSite] = useState({
    nom_site: '',
    type_site: 'vitrine',
    use_erp_data: true,
    couleur_principale: '#22c55e',
    couleur_secondaire: '#16a34a',
    slogan: '',
    description: '',
    style_theme: 'moderne',
    pages: ['accueil', 'produits', 'a-propos', 'contact'],
    // Informations entreprise
    adresse: '',
    telephone: '',
    email_contact: '',
    horaires: '',
    // Réseaux sociaux
    facebook: '',
    instagram: '',
    linkedin: '',
    // Contenu "À propos"
    histoire: '',
    valeurs: '',
    certifications: ''
  });
  
  // Influenceurs
  const [influenceurs, setInfluenceurs] = useState({ predefinis: [], personnalises: [] });
  const [selectedInfluenceurs, setSelectedInfluenceurs] = useState([]); // Multi-sélection
  const [influenceurCustomDescription, setInfluenceurCustomDescription] = useState(''); // Description libre
  
  // Génération contenu
  const [showGenerateContent, setShowGenerateContent] = useState(false);
  const [contentRequest, setContentRequest] = useState({
    type_contenu: 'post',
    plateforme: 'instagram',
    sujet: '',
    ton: 'professionnel'
  });
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Génération vidéo
  const [showGenerateVideo, setShowGenerateVideo] = useState(false);
  const [videoRequest, setVideoRequest] = useState({
    type_video: 'reel',
    sujet: '',
    duree_cible: 60,
    style_visuel: 'moderne'
  });
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  
  // Config complète du studio (incluant influenceurs prédéfinis)
  const [studioConfig, setStudioConfig] = useState(null);
  
  // Guide
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchTemplates();
    fetchInfluenceurs();
    fetchStudioConfig();
    fetchSites();
    fetchGuide();
    fetchVideos();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/studio-ia/dashboard');
      setDashboard(res.data);
    } catch (err) {
      console.log('Erreur dashboard:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/studio-ia/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.log('Erreur templates:', err);
    }
  };

  const fetchInfluenceurs = async () => {
    try {
      const res = await api.get('/studio-ia/influenceurs');
      setInfluenceurs(prev => ({ ...prev, personnalises: res.data.influenceurs || [] }));
    } catch (err) {
      console.log('Erreur influenceurs:', err);
    }
  };
  
  const fetchStudioConfig = async () => {
    try {
      const res = await api.get('/studio-ia/config');
      setStudioConfig(res.data);
      // Charger les influenceurs prédéfinis depuis config
      if (res.data.influenceurs_predefinis) {
        setInfluenceurs(prev => ({ ...prev, predefinis: res.data.influenceurs_predefinis }));
      }
    } catch (err) {
      console.log('Erreur config:', err);
    }
  };
  
  const fetchGuide = async () => {
    try {
      const res = await api.get('/studio-ia/guide');
      setGuide(res.data);
    } catch (err) {
      console.log('Erreur guide:', err);
    }
  };
  
  const fetchVideos = async () => {
    try {
      const res = await api.get('/studio-ia/videos');
      setVideos(res.data.videos || []);
    } catch (err) {
      console.log('Erreur videos:', err);
    }
  };
  
  const refreshVideoStatus = useCallback(async (videoId) => {
    try {
      const res = await api.get(`/studio-ia/video/${videoId}/status`);
      setVideos(prev => prev.map(v => v.id === videoId ? res.data : v));
      if (res.data.statut === 'termine') {
        toast.success('Vidéo générée avec succès !');
      }
    } catch (err) {
      console.log('Erreur refresh video:', err);
    }
  }, []);
  
  const retryVideo = useCallback(async (videoId) => {
    try {
      const res = await api.post(`/studio-ia/video/${videoId}/retry`);
      toast.success('Génération relancée !');
      // Rafraîchir le statut après le lancement
      await refreshVideoStatus(videoId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la relance');
    }
  }, [refreshVideoStatus]);
  
  const deleteVideo = useCallback(async (videoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) return;
    try {
      await api.delete(`/studio-ia/video/${videoId}`);
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Vidéo supprimée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  }, []);

  const fetchSites = async () => {
    try {
      const res = await api.get('/studio-ia/sites');
      setSites(res.data.sites || []);
    } catch (err) {
      console.log('Erreur sites:', err);
    }
  };

  const handleCreateSite = async () => {
    if (!newSite.nom_site) {
      toast.error('Nom du site requis');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/studio-ia/sites/create', {
        ...newSite,
        template_id: selectedTemplate?.id
      });
      
      toast.success('Site créé avec succès !');
      setShowCreateSite(false);
      fetchSites();
      fetchDashboard();
      setNewSite({
        nom_site: '',
        type_site: 'vitrine',
        use_erp_data: true,
        couleur_principale: '#22c55e',
        slogan: '',
        pages: ['accueil', 'produits', 'a-propos', 'contact']
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création site');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContent = async () => {
    if (selectedInfluenceurs.length === 0 && !influenceurCustomDescription) {
      toast.error('Sélectionnez un influenceur ou décrivez-le');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await api.post('/studio-ia/content/generate', {
        influenceur_id: selectedInfluenceurs[0]?.id || null,
        influenceur_custom: influenceurCustomDescription || null,
        ...contentRequest
      });
      
      setGeneratedContent(res.data.content);
      toast.success('Contenu généré !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (selectedInfluenceurs.length === 0 && !influenceurCustomDescription) {
      toast.error('Sélectionnez au moins un influenceur ou décrivez celui que vous souhaitez');
      return;
    }
    
    if (!videoRequest.sujet) {
      toast.error('Décrivez le sujet de la vidéo');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await api.post('/studio-ia/video/generate', {
        influenceurs: selectedInfluenceurs.map(i => i.id),
        influenceur_custom: influenceurCustomDescription || null,
        ...videoRequest
      });
      
      setGeneratedVideo(res.data);
      toast.success('Génération vidéo lancée ! Cela peut prendre 3-5 minutes.');
      setShowGenerateVideo(false);
      
      // Réinitialiser les sélections
      setSelectedInfluenceurs([]);
      setInfluenceurCustomDescription('');
      
      // Ajouter la nouvelle vidéo à la liste
      fetchVideos();
      
      // Rafraîchir le statut périodiquement
      const videoId = res.data.video_id;
      const checkInterval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/studio-ia/video/${videoId}/status`);
          setVideos(prev => prev.map(v => v.id === videoId ? statusRes.data : v));
          
          if (statusRes.data.statut === 'termine' || statusRes.data.statut === 'erreur') {
            clearInterval(checkInterval);
            if (statusRes.data.statut === 'termine') {
              toast.success('Votre vidéo est prête !');
            }
          }
        } catch (e) {
          clearInterval(checkInterval);
        }
      }, 10000); // Vérifier toutes les 10 secondes
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération vidéo');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="studio-ia-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            Studio IA & Sites
          </h1>
          <p className="text-muted-foreground mt-1">
            Créez vos sites web, générez du contenu avec vos influenceurs IA
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setActiveTab('guide')} data-testid="help-btn">
            <HelpCircle className="w-4 h-4 mr-2" />
            Aide
          </Button>
          <Button variant="outline" onClick={() => setShowGenerateContent(true)} data-testid="generate-content-btn">
            <FileText className="w-4 h-4 mr-2" />
            Générer contenu
          </Button>
          <Button variant="outline" onClick={() => setShowGenerateVideo(true)} data-testid="create-video-btn">
            <Video className="w-4 h-4 mr-2" />
            Créer vidéo
          </Button>
          <Button onClick={() => setShowCreateSite(true)} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" data-testid="new-site-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau site
          </Button>
        </div>
      </div>

      {/* Stats rapides */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.statistiques?.sites || 0}</p>
                  <p className="text-sm text-muted-foreground">Sites actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.statistiques?.contenus_generes || 0}</p>
                  <p className="text-sm text-muted-foreground">Contenus générés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.statistiques?.videos || 0}</p>
                  <p className="text-sm text-muted-foreground">Vidéos créées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.statistiques?.leads_captures || 0}</p>
                  <p className="text-sm text-muted-foreground">Leads capturés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="sites" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Sites</span>
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            <span className="hidden sm:inline">Vidéos</span>
          </TabsTrigger>
          <TabsTrigger value="influenceurs" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Influenceurs</span>
          </TabsTrigger>
          <TabsTrigger value="contenus" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Contenus</span>
          </TabsTrigger>
          <TabsTrigger value="guide" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Guide</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Actions rapides */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Actions rapides
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowCreateSite(true)}>
                  <Globe className="w-6 h-6 mb-2 text-blue-500" />
                  <span>Créer un site</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowGenerateContent(true)}>
                  <FileText className="w-6 h-6 mb-2 text-purple-500" />
                  <span>Générer un post</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowGenerateVideo(true)}>
                  <Film className="w-6 h-6 mb-2 text-pink-500" />
                  <span>Créer un réel</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveTab('guide')}>
                  <HelpCircle className="w-6 h-6 mb-2 text-green-500" />
                  <span>Aide & Guide</span>
                </Button>
              </CardContent>
            </Card>

            {/* Influenceurs disponibles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Vos Influenceurs IA
                </CardTitle>
                <CardDescription>
                  Choisissez un influenceur pour générer du contenu
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {influenceurs.predefinis?.slice(0, 3).map((inf) => (
                  <div 
                    key={inf.id} 
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedInfluenceurs.some(s => s.id === inf.id) ? 'bg-primary/10 border border-primary' : 'bg-muted/50 hover:bg-muted'
                    }`}
                    onClick={() => {
                      const isSelected = selectedInfluenceurs.some(s => s.id === inf.id);
                      if (isSelected) {
                        setSelectedInfluenceurs(prev => prev.filter(s => s.id !== inf.id));
                      } else {
                        setSelectedInfluenceurs(prev => [...prev, inf]);
                      }
                    }}
                  >
                    <InfluenceurAvatar influenceur={inf} size="sm" />
                    <div className="flex-1">
                      <p className="font-medium">{inf.nom}</p>
                      <p className="text-xs text-muted-foreground">{inf.style_communication}</p>
                    </div>
                    {selectedInfluenceurs.some(s => s.id === inf.id) && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Sites */}
        <TabsContent value="sites" className="space-y-6">
          {sites.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Globe className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Aucun site créé</h3>
                <p className="text-muted-foreground mb-4">
                  Créez votre premier site web en quelques clics
                </p>
                <Button onClick={() => setShowCreateSite(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer mon premier site
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((site) => (
                <Card key={site.id} className="overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Globe className="w-12 h-12 text-primary/50" />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{site.nom}</h3>
                        <p className="text-sm text-muted-foreground">{site.url_preview}</p>
                      </div>
                      <Badge variant={site.publie ? 'default' : 'secondary'}>
                        {site.publie ? 'En ligne' : 'Brouillon'}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Eye className="w-4 h-4 mr-1" />
                        Aperçu
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab Influenceurs */}
        <TabsContent value="influenceurs" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            {influenceurs.predefinis?.map((inf) => (
              <InfluenceurCard
                key={inf.id}
                influenceur={inf}
                onSelect={(influenceur) => {
                  const isSelected = selectedInfluenceurs.some(s => s.id === influenceur.id);
                  if (isSelected) {
                    setSelectedInfluenceurs(prev => prev.filter(s => s.id !== influenceur.id));
                  } else {
                    setSelectedInfluenceurs(prev => [...prev, influenceur]);
                  }
                }}
                selected={selectedInfluenceurs.some(s => s.id === inf.id)}
              />
            ))}
          </div>
        </TabsContent>

        {/* Tab Contenus */}
        <TabsContent value="contenus" className="space-y-6">
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Générez du contenu</h3>
              <p className="text-muted-foreground mb-4">
                Utilisez vos influenceurs IA pour créer des posts, vidéos et plus encore
              </p>
              <Button onClick={() => setShowGenerateContent(true)}>
                <Sparkles className="w-4 h-4 mr-2" />
                Générer du contenu
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Vidéos */}
        <TabsContent value="videos" className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Mes Vidéos IA</h2>
              <p className="text-sm text-muted-foreground">Vidéos générées avec vos influenceurs virtuels</p>
            </div>
            <Button onClick={() => setShowGenerateVideo(true)} data-testid="create-video-tab-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle vidéo
            </Button>
          </div>
          
          {videos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Aucune vidéo créée</h3>
                <p className="text-muted-foreground mb-4">
                  Créez votre première vidéo avec un influenceur IA
                </p>
                <Button onClick={() => setShowGenerateVideo(true)}>
                  <Film className="w-4 h-4 mr-2" />
                  Créer ma première vidéo
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <VideoCard 
                  key={video.id} 
                  video={video}
                  onRefresh={refreshVideoStatus}
                  onRetry={retryVideo}
                  onDelete={deleteVideo}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Tab Guide */}
        <TabsContent value="guide" className="space-y-6">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="mb-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shrink-0">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-2">{guide?.sections?.introduction?.titre || 'Bienvenue dans le Studio IA'}</h2>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <p className="text-muted-foreground whitespace-pre-line">
                        {guide?.sections?.introduction?.contenu || 'Le Studio IA est votre outil pour créer votre présence en ligne.'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sections du guide */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Créer un site */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-500" />
                    {guide?.sections?.creer_site?.titre || 'Créer votre site web'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {guide?.sections?.creer_site?.etapes?.map((etape) => (
                    <GuideStep key={etape.numero} {...etape} />
                  )) || (
                    <>
                      <GuideStep numero={1} titre="Cliquez sur 'Nouveau site'" description="Le bouton se trouve en haut à droite." />
                      <GuideStep numero={2} titre="Choisissez le type" description="Site vitrine, E-commerce ou Landing page." />
                      <GuideStep numero={3} titre="Validez" description="L'IA génère votre site automatiquement." />
                    </>
                  )}
                  {guide?.sections?.creer_site?.conseil && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 mt-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800 dark:text-green-200">{guide.sections.creer_site.conseil}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Générer du contenu */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-500" />
                    {guide?.sections?.generer_contenu?.titre || 'Générer du contenu'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {guide?.sections?.generer_contenu?.etapes?.map((etape) => (
                    <GuideStep key={etape.numero} {...etape} />
                  )) || (
                    <>
                      <GuideStep numero={1} titre="Choisissez un influenceur" description="Sélectionnez le personnage qui vous représentera." />
                      <GuideStep numero={2} titre="Cliquez 'Générer contenu'" description="Choisissez le type : post, vidéo, email..." />
                      <GuideStep numero={3} titre="Décrivez le sujet" description="L'IA rédige le texte pour vous." />
                    </>
                  )}
                  {guide?.sections?.generer_contenu?.conseil && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 mt-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800 dark:text-green-200">{guide.sections.generer_contenu.conseil}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Créer une vidéo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-pink-500" />
                    {guide?.sections?.creer_video?.titre || 'Créer une vidéo IA'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {guide?.sections?.creer_video?.etapes?.map((etape) => (
                    <GuideStep key={etape.numero} {...etape} />
                  )) || (
                    <>
                      <GuideStep numero={1} titre="Sélectionnez un influenceur" description="C'est lui qui présentera votre vidéo." />
                      <GuideStep numero={2} titre="Choisissez le type" description="Réel, présentation, témoignage..." />
                      <GuideStep numero={3} titre="Décrivez le sujet" description="La génération prend 3-5 minutes." />
                    </>
                  )}
                  {guide?.sections?.creer_video?.attention && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 mt-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">{guide.sections.creer_video.attention}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Les influenceurs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-500" />
                    {guide?.sections?.influenceurs?.titre || 'Les influenceurs IA'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="text-muted-foreground whitespace-pre-line">
                      {guide?.sections?.influenceurs?.explication || `Les influenceurs IA sont des personnages virtuels qui communiquent pour vous. Chacun a sa personnalité et son style.`}
                    </p>
                  </div>
                  {guide?.sections?.influenceurs?.conseil && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 mt-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800 dark:text-green-200">{guide.sections.influenceurs.conseil}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* FAQ */}
            {guide?.sections?.faq && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-orange-500" />
                    {guide.sections.faq.titre}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {guide.sections.faq.questions?.map((q, i) => (
                    <div key={i} className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-primary" />
                        {q.question}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-2 ml-6">{q.reponse}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Création Site */}
      <Dialog open={showCreateSite} onOpenChange={setShowCreateSite}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Créer un nouveau site web
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Type de site */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">1. Type de site</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'vitrine', label: 'Site Vitrine', icon: Globe, desc: 'Présentez votre exploitation et vos activités' },
                  { value: 'ecommerce', label: 'E-commerce', icon: ShoppingCart, desc: 'Vendez vos produits en direct aux particuliers' },
                  { value: 'landing', label: 'Landing Page', icon: Megaphone, desc: 'Page unique pour une campagne marketing' }
                ].map(type => (
                  <Card 
                    key={type.value}
                    className={`cursor-pointer transition-all hover:shadow-md ${newSite.type_site === type.value ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                    onClick={() => setNewSite({...newSite, type_site: type.value})}
                  >
                    <CardContent className="p-4 text-center">
                      <type.icon className={`w-8 h-8 mx-auto mb-2 ${newSite.type_site === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                      <p className="font-medium">{type.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{type.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Informations de base */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                2. Informations de base
                <span className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Mic className="w-3 h-3" /> Cliquez sur le micro pour dicter
                </span>
              </Label>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du site *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newSite.nom_site}
                      onChange={(e) => setNewSite({...newSite, nom_site: e.target.value})}
                      placeholder="Ex: Domaine des Vignes, Ferme de la Vallée..."
                      className="flex-1"
                    />
                    <VoiceInput onTranscript={(text) => setNewSite({...newSite, nom_site: newSite.nom_site ? `${newSite.nom_site} ${text}` : text})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Slogan / Accroche</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newSite.slogan}
                      onChange={(e) => setNewSite({...newSite, slogan: e.target.value})}
                      placeholder="Ex: Le goût authentique du terroir depuis 1952"
                      className="flex-1"
                    />
                    <VoiceInput onTranscript={(text) => setNewSite({...newSite, slogan: newSite.slogan ? `${newSite.slogan} ${text}` : text})} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description du site</Label>
                <div className="flex gap-2">
                  <Textarea
                    value={newSite.description}
                    onChange={(e) => setNewSite({...newSite, description: e.target.value})}
                    placeholder="Décrivez votre activité, vos produits phares, ce qui vous rend unique... Cette description sera utilisée pour générer le contenu de votre site."
                    rows={3}
                    className="flex-1"
                  />
                  <VoiceInput 
                    onTranscript={(text) => setNewSite({...newSite, description: newSite.description ? `${newSite.description} ${text}` : text})} 
                    className="self-start"
                  />
                </div>
              </div>
            </div>

            {/* Style et couleurs */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">3. Style et apparence</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Thème visuel</Label>
                  <Select 
                    value={newSite.style_theme} 
                    onValueChange={(v) => setNewSite({...newSite, style_theme: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="moderne">🎨 Moderne & Épuré</SelectItem>
                      <SelectItem value="naturel">🌿 Naturel & Bio</SelectItem>
                      <SelectItem value="terroir">🍷 Terroir & Tradition</SelectItem>
                      <SelectItem value="premium">✨ Premium & Luxe</SelectItem>
                      <SelectItem value="rustique">🏡 Rustique & Authentique</SelectItem>
                      <SelectItem value="minimaliste">◻️ Minimaliste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Couleurs</Label>
                  <div className="flex gap-3">
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="color"
                        value={newSite.couleur_principale}
                        onChange={(e) => setNewSite({...newSite, couleur_principale: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer border"
                      />
                      <span className="text-sm text-muted-foreground">Principale</span>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="color"
                        value={newSite.couleur_secondaire}
                        onChange={(e) => setNewSite({...newSite, couleur_secondaire: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer border"
                      />
                      <span className="text-sm text-muted-foreground">Secondaire</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pages à inclure */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">4. Pages à créer</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { id: 'accueil', label: 'Accueil', icon: '🏠', required: true },
                  { id: 'produits', label: 'Nos Produits', icon: '🛒' },
                  { id: 'services', label: 'Services', icon: '⚙️' },
                  { id: 'a-propos', label: 'À Propos', icon: '📖' },
                  { id: 'galerie', label: 'Galerie Photos', icon: '📸' },
                  { id: 'actualites', label: 'Actualités', icon: '📰' },
                  { id: 'temoignages', label: 'Témoignages', icon: '💬' },
                  { id: 'contact', label: 'Contact', icon: '✉️' },
                ].map(page => (
                  <div
                    key={page.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      newSite.pages.includes(page.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted hover:border-primary/50'
                    } ${page.required ? 'opacity-75' : ''}`}
                    onClick={() => {
                      if (page.required) return;
                      const pages = newSite.pages.includes(page.id)
                        ? newSite.pages.filter(p => p !== page.id)
                        : [...newSite.pages, page.id];
                      setNewSite({...newSite, pages});
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{page.icon}</span>
                      <span className="text-sm font-medium">{page.label}</span>
                      {page.required && <Badge variant="secondary" className="text-xs">Obligatoire</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coordonnées */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">5. Coordonnées (pour la page Contact)</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={newSite.adresse}
                    onChange={(e) => setNewSite({...newSite, adresse: e.target.value})}
                    placeholder="Ex: 415 Chemin du Routas, 26110 Sainte-Jalle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newSite.telephone}
                    onChange={(e) => setNewSite({...newSite, telephone: e.target.value})}
                    placeholder="Ex: 06 85 26 61 93"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email de contact</Label>
                  <Input
                    value={newSite.email_contact}
                    onChange={(e) => setNewSite({...newSite, email_contact: e.target.value})}
                    placeholder="Ex: contact@mondomaine.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horaires d'ouverture</Label>
                  <Input
                    value={newSite.horaires}
                    onChange={(e) => setNewSite({...newSite, horaires: e.target.value})}
                    placeholder="Ex: Lun-Ven 9h-18h, Sam 10h-16h"
                  />
                </div>
              </div>
            </div>

            {/* Réseaux sociaux */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">6. Réseaux sociaux (optionnel)</Label>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                  </Label>
                  <Input
                    value={newSite.facebook}
                    onChange={(e) => setNewSite({...newSite, facebook: e.target.value})}
                    placeholder="URL ou nom de page"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-600" /> Instagram
                  </Label>
                  <Input
                    value={newSite.instagram}
                    onChange={(e) => setNewSite({...newSite, instagram: e.target.value})}
                    placeholder="@votre_compte"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
                  </Label>
                  <Input
                    value={newSite.linkedin}
                    onChange={(e) => setNewSite({...newSite, linkedin: e.target.value})}
                    placeholder="URL du profil"
                  />
                </div>
              </div>
            </div>

            {/* Histoire et valeurs (pour page À propos) */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">7. Votre histoire (pour la page À Propos)</Label>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Histoire de l'exploitation</Label>
                  <Textarea
                    value={newSite.histoire}
                    onChange={(e) => setNewSite({...newSite, histoire: e.target.value})}
                    placeholder="Racontez l'histoire de votre exploitation : quand a-t-elle été créée ? Par qui ? Quelle est son évolution ?"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vos valeurs</Label>
                  <Textarea
                    value={newSite.valeurs}
                    onChange={(e) => setNewSite({...newSite, valeurs: e.target.value})}
                    placeholder="Quelles sont vos valeurs ? Respect de l'environnement, qualité, tradition familiale, innovation..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Certifications & Labels</Label>
                  <Input
                    value={newSite.certifications}
                    onChange={(e) => setNewSite({...newSite, certifications: e.target.value})}
                    placeholder="Ex: Bio, HVE, AOC, Label Rouge..."
                  />
                </div>
              </div>
            </div>

            {/* Templates */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">8. Choisissez un template</Label>
              <div className="grid md:grid-cols-3 gap-3">
                {templates.filter(t => t.type_site === newSite.type_site).map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onSelect={setSelectedTemplate}
                    selected={selectedTemplate?.id === template.id}
                  />
                ))}
              </div>
            </div>

            {/* Options ERP */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label className="font-medium">Utiliser les données de l'ERP</Label>
                <p className="text-sm text-muted-foreground">
                  Importer automatiquement vos produits, parcelles et informations depuis Bureau Agricole
                </p>
              </div>
              <Switch
                checked={newSite.use_erp_data}
                onCheckedChange={(v) => setNewSite({...newSite, use_erp_data: v})}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateSite(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateSite} disabled={loading || !newSite.nom_site}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Générer mon site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Génération Contenu */}
      <Dialog open={showGenerateContent} onOpenChange={setShowGenerateContent}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Générer du contenu IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Sélection influenceur */}
            <div className="space-y-2">
              <Label>Influenceur IA</Label>
              <div className="grid grid-cols-2 gap-2">
                {influenceurs.predefinis?.slice(0, 4).map((inf) => (
                  <div 
                    key={inf.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedInfluenceurs.some(s => s.id === inf.id) ? 'bg-primary/10 border border-primary' : 'bg-muted/50 hover:bg-muted'
                    }`}
                    onClick={() => {
                      const isSelected = selectedInfluenceurs.some(s => s.id === inf.id);
                      if (isSelected) {
                        setSelectedInfluenceurs(prev => prev.filter(s => s.id !== inf.id));
                      } else {
                        setSelectedInfluenceurs(prev => [...prev, inf]);
                      }
                    }}
                  >
                    <InfluenceurAvatar influenceur={inf} size="sm" />
                    <span className="text-sm font-medium">{inf.nom?.split(' - ')[0] || inf.nom}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Type de contenu */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contenu</Label>
                <Select 
                  value={contentRequest.type_contenu}
                  onValueChange={(v) => setContentRequest({...contentRequest, type_contenu: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">📱 Post réseaux sociaux</SelectItem>
                    <SelectItem value="reel">🎬 Script Réel/TikTok</SelectItem>
                    <SelectItem value="description_produit">📦 Description produit</SelectItem>
                    <SelectItem value="email">✉️ Email marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Plateforme</Label>
                <Select 
                  value={contentRequest.plateforme}
                  onValueChange={(v) => setContentRequest({...contentRequest, plateforme: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sujet */}
            <div className="space-y-2">
              <Label>Sujet / Thème</Label>
              <Textarea
                value={contentRequest.sujet}
                onChange={(e) => setContentRequest({...contentRequest, sujet: e.target.value})}
                placeholder="Ex: Nouvelle récolte de lavande, promotion été, coulisses de l'exploitation..."
                rows={3}
              />
            </div>

            {/* Résultat */}
            {generatedContent && (
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Contenu généré
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {generatedContent.texte && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Texte</Label>
                      <p className="mt-1 whitespace-pre-wrap">{generatedContent.texte}</p>
                    </div>
                  )}
                  {generatedContent.hook && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Hook (accroche)</Label>
                      <p className="mt-1 font-semibold">{generatedContent.hook}</p>
                    </div>
                  )}
                  {generatedContent.script_complet && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Script complet</Label>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{generatedContent.script_complet}</p>
                    </div>
                  )}
                  {generatedContent.hashtags && (
                    <div className="flex flex-wrap gap-1">
                      {generatedContent.hashtags.map((h, i) => (
                        <Badge key={i} variant="secondary">#{h}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline">
                      <Copy className="w-4 h-4 mr-1" />
                      Copier
                    </Button>
                    <Button size="sm" variant="outline">
                      <Calendar className="w-4 h-4 mr-1" />
                      Planifier
                    </Button>
                    <Button size="sm">
                      <Send className="w-4 h-4 mr-1" />
                      Publier
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGenerateContent(false);
              setGeneratedContent(null);
            }}>
              Fermer
            </Button>
            <Button onClick={handleGenerateContent} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Génération Vidéo - Version améliorée */}
      <Dialog open={showGenerateVideo} onOpenChange={setShowGenerateVideo}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-pink-500" />
              Créer une vidéo IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Section 1: Choix des influenceurs */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" /> 1. Choisissez vos influenceurs
                </Label>
                <Badge variant="outline">{selectedInfluenceurs.length} sélectionné(s)</Badge>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Cliquez pour sélectionner un ou plusieurs influenceurs, ou décrivez celui que vous souhaitez ci-dessous.
              </p>
              
              {/* Influenceurs prédéfinis */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(influenceurs.predefinis?.length > 0 ? influenceurs.predefinis : [
                  { id: "marie", nom: "Marie", description: "Agricultrice bio passionnée", genre: "femme", age: "35 ans", environnement: "Ferme bio" },
                  { id: "jean", nom: "Jean", description: "Viticulteur expert", genre: "homme", age: "45 ans", environnement: "Vignoble" },
                  { id: "sophie", nom: "Sophie", description: "Experte en maraîchage", genre: "femme", age: "30 ans", environnement: "Potager" },
                  { id: "pierre", nom: "Pierre", description: "Céréalier innovant", genre: "homme", age: "40 ans", environnement: "Champ de blé" },
                  { id: "claire", nom: "Claire", description: "Apicultrice artisanale", genre: "femme", age: "28 ans", environnement: "Rucher" },
                  { id: "thomas", nom: "Thomas", description: "Éleveur responsable", genre: "homme", age: "38 ans", environnement: "Prairie" }
                ]).map((inf) => {
                  const isSelected = selectedInfluenceurs.some(s => s.id === inf.id);
                  return (
                    <div 
                      key={inf.id}
                      className={`relative p-3 rounded-xl cursor-pointer transition-all border-2 ${
                        isSelected 
                          ? 'border-pink-500 bg-pink-50 dark:bg-pink-950/20 shadow-md' 
                          : 'border-transparent bg-muted/50 hover:bg-muted hover:border-pink-200'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedInfluenceurs(prev => prev.filter(s => s.id !== inf.id));
                        } else {
                          setSelectedInfluenceurs(prev => [...prev, inf]);
                        }
                      }}
                    >
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                          inf.genre === 'femme' ? 'bg-pink-100' : 'bg-blue-100'
                        }`}>
                          {inf.icone || (inf.genre === 'femme' ? '👩' : '👨')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{inf.nom}</p>
                          <p className="text-xs text-muted-foreground truncate">{inf.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge variant="secondary" className="text-xs">{inf.age}</Badge>
                        <Badge variant="secondary" className="text-xs">{inf.environnement}</Badge>
                      </div>
                    </div>
                  );
                })}
                
                {/* Mes influenceurs personnalisés */}
                {influenceurs.personnalises?.map((inf) => {
                  const isSelected = selectedInfluenceurs.some(s => s.id === inf.id);
                  return (
                    <div 
                      key={inf.id}
                      className={`relative p-3 rounded-xl cursor-pointer transition-all border-2 ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/20 shadow-md' 
                          : 'border-transparent bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-200'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedInfluenceurs(prev => prev.filter(s => s.id !== inf.id));
                        } else {
                          setSelectedInfluenceurs(prev => [...prev, inf]);
                        }
                      }}
                    >
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                          {inf.nom?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-semibold">{inf.nom}</p>
                          <Badge className="text-xs bg-purple-100 text-purple-700">Personnalisé</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Description libre d'un influenceur */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-xl border border-amber-200">
                <Label className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <Wand2 className="w-4 h-4" /> Ou décrivez l'influenceur de vos rêves
                </Label>
                <Textarea
                  value={influenceurCustomDescription}
                  onChange={(e) => setInfluenceurCustomDescription(e.target.value)}
                  placeholder="Ex: Une femme de 35 ans, blonde, souriante, en tenue d'agricultrice moderne, dans un champ de lavande au coucher du soleil..."
                  rows={3}
                  className="mt-2 bg-white dark:bg-background"
                />
                <p className="text-xs text-amber-600 mt-2">
                  L'IA créera un influenceur personnalisé basé sur votre description.
                </p>
              </div>
            </div>
            
            {/* Section 2: Configuration vidéo */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Settings className="w-4 h-4" /> 2. Configuration de la vidéo
              </Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de vidéo</Label>
                  <Select 
                    value={videoRequest.type_video}
                    onValueChange={(v) => setVideoRequest({...videoRequest, type_video: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reel">🎬 Réel / TikTok / Shorts</SelectItem>
                      <SelectItem value="presentation">🎤 Présentation produit</SelectItem>
                      <SelectItem value="temoignage">💬 Témoignage client</SelectItem>
                      <SelectItem value="tutoriel">📚 Tutoriel / How-to</SelectItem>
                      <SelectItem value="promo">🔥 Promotion / Offre</SelectItem>
                      <SelectItem value="storytelling">📖 Storytelling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Durée de la vidéo</Label>
                  <Select 
                    value={videoRequest.duree_cible.toString()}
                    onValueChange={(v) => setVideoRequest({...videoRequest, duree_cible: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 secondes (TikTok court)</SelectItem>
                      <SelectItem value="30">30 secondes (Réel)</SelectItem>
                      <SelectItem value="60">1 minute</SelectItem>
                      <SelectItem value="90">1 min 30</SelectItem>
                      <SelectItem value="120">2 minutes</SelectItem>
                      <SelectItem value="180">3 minutes</SelectItem>
                      <SelectItem value="300">5 minutes (Long format)</SelectItem>
                      <SelectItem value="600">10 minutes (YouTube)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Section 3: Sujet de la vidéo */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" /> 3. Décrivez votre vidéo
              </Label>
              
              <Textarea
                value={videoRequest.sujet}
                onChange={(e) => setVideoRequest({...videoRequest, sujet: e.target.value})}
                placeholder="Décrivez en détail le sujet de votre vidéo...

Ex: Présentation de notre nouvelle gamme de miels bio. Montrer les ruches, le processus de récolte, et les différentes variétés. Terminer par un appel à visiter notre boutique en ligne."
                rows={5}
                className="text-base"
              />
            </div>

            {/* Résumé de la sélection */}
            {(selectedInfluenceurs.length > 0 || influenceurCustomDescription) && (
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-2">
                    <Check className="w-5 h-5" />
                    <span className="font-semibold">Prêt à générer</span>
                  </div>
                  <div className="text-sm space-y-1">
                    {selectedInfluenceurs.length > 0 && (
                      <p>Influenceurs : {selectedInfluenceurs.map(i => i.nom).join(', ')}</p>
                    )}
                    {influenceurCustomDescription && (
                      <p>+ Influenceur personnalisé selon votre description</p>
                    )}
                    <p>Durée : {videoRequest.duree_cible} secondes</p>
                    <p>Type : {videoRequest.type_video}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowGenerateVideo(false);
              setGeneratedVideo(null);
              setSelectedInfluenceurs([]);
              setInfluenceurCustomDescription('');
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleGenerateVideo} 
              disabled={generating || (!selectedInfluenceurs.length && !influenceurCustomDescription) || !videoRequest.sujet}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Générer la vidéo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
